/**
 * Follow-Up Queue widget — summary of the latest sales-inbox follow-up scan.
 * Phase 1: shows scan counts and freshness; thread-level drill-down lives in
 * the existing ScheduledEmailsTab and is intentionally not duplicated here.
 */

import React from 'react';
import { StatusPill } from '../ui';
import type { StatusVariant } from '../ui';
import { FollowUpQueueSummary } from '../../services/salesApi';
import WidgetHeader from './WidgetHeader';

interface Props {
  loading: boolean;
  error: string | null;
  summary: FollowUpQueueSummary | null;
}

const formatTimestamp = (iso: string | null): string => {
  if (!iso) return 'Never';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const freshnessVariant = (days: number | null): StatusVariant => {
  if (days === null) return 'draft';
  if (days <= 1) return 'live';
  if (days <= 3) return 'approved';
  if (days <= 7) return 'pending';
  return 'flagged';
};

const FollowUpQueue: React.FC<Props> = ({ loading, error, summary }) => {
  return (
    <div style={styles.container}>
      <WidgetHeader
        title="Follow-up queue"
        subtitle="Latest sales-inbox scan results"
        right={
          summary && (
            <StatusPill variant={freshnessVariant(summary.daysSinceLastRun)} size="sm">
              {summary.daysSinceLastRun === null
                ? 'No runs yet'
                : summary.daysSinceLastRun === 0
                  ? 'Today'
                  : `${summary.daysSinceLastRun}d ago`}
            </StatusPill>
          )
        }
      />

      {error && <div style={styles.error}>{error}</div>}

      {!loading && summary && (
        <>
          <div style={styles.statRow}>
            <Stat
              label="Type A — awaiting their reply"
              value={summary.typeACount}
              tone="warning"
              hint="We sent last — nudge the counterparty"
            />
            <Stat
              label="Type B — we owe a reply"
              value={summary.typeBCount}
              tone="danger"
              hint="They sent last — respond"
            />
            <Stat
              label="New leads"
              value={summary.typeDCount ?? 0}
              tone="success"
              hint="Inbound — nobody has replied yet"
            />
            <Stat
              label="Threads scanned"
              value={summary.totalThreadsScanned}
              tone="muted"
              hint="In the last scan"
            />
          </div>
          <div style={styles.footnote}>
            Last scan: {formatTimestamp(summary.lastRunAt)}
            {summary.lastRunStatus && summary.lastRunStatus !== 'completed' && (
              <span style={{ color: 'var(--danger)', marginLeft: 8 }}>
                · status: {summary.lastRunStatus}
              </span>
            )}
          </div>
        </>
      )}

      {!loading && !summary && (
        <div style={styles.empty}>
          No follow-up scan data yet. Configure the scanner in Admin → Scheduled Emails.
        </div>
      )}
    </div>
  );
};

interface StatProps {
  label: string;
  value: number;
  tone: 'danger' | 'warning' | 'success' | 'muted';
  hint?: string;
}

const Stat: React.FC<StatProps> = ({ label, value, tone, hint }) => {
  const colorMap: Record<StatProps['tone'], string> = {
    danger: 'var(--danger)',
    warning: 'var(--warning)',
    success: 'var(--success)',
    muted: 'var(--fg-2)',
  };
  return (
    <div style={styles.stat}>
      <div style={styles.statLabel}>{label}</div>
      <div style={{ ...styles.statValue, color: colorMap[tone] }}>{value}</div>
      {hint && <div style={styles.statHint}>{hint}</div>}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: { padding: 20, display: 'flex', flexDirection: 'column', gap: 14 },
  statRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: 12,
  },
  stat: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    padding: 14,
    border: '1px solid var(--border-1)',
    borderRadius: 'var(--radius-md)',
    background: 'var(--bg-surface-1)',
  },
  statLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'var(--fg-4)',
  },
  statValue: {
    fontSize: 28,
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1.05,
    marginTop: 2,
  },
  statHint: { fontSize: 11.5, color: 'var(--fg-4)' },
  footnote: { fontSize: 11.5, color: 'var(--fg-4)', textAlign: 'right' },
  error: {
    padding: '10px 12px',
    background: 'var(--danger-soft)',
    color: 'var(--danger)',
    borderRadius: 'var(--radius-md)',
    fontSize: 13,
  },
  empty: {
    padding: '24px 12px',
    textAlign: 'center',
    color: 'var(--fg-4)',
    fontSize: 13,
  },
};

export default FollowUpQueue;
